import { INITIAL_EVENTS, INITIAL_CATEGORIES, INITIAL_MEDIA_ITEMS, TIMELINE_DECADES } from './jubileeData';

const STORAGE_KEYS = {
  MEDIA: 'jemparc_jubilee_media_items_v1',
  EVENTS: 'jemparc_jubilee_events_v1',
  CATEGORIES: 'jemparc_jubilee_categories_v1',
};

// Initialize storage if empty
const initializeStorage = () => {
  if (typeof window === 'undefined') return;
  try {
    if (!localStorage.getItem(STORAGE_KEYS.MEDIA)) {
      localStorage.setItem(STORAGE_KEYS.MEDIA, JSON.stringify(INITIAL_MEDIA_ITEMS));
    }
    if (!localStorage.getItem(STORAGE_KEYS.EVENTS)) {
      localStorage.setItem(STORAGE_KEYS.EVENTS, JSON.stringify(INITIAL_EVENTS));
    }
    if (!localStorage.getItem(STORAGE_KEYS.CATEGORIES)) {
      localStorage.setItem(STORAGE_KEYS.CATEGORIES, JSON.stringify(INITIAL_CATEGORIES));
    }
  } catch (e) {
    console.error('Error initializing Jubilee LocalStorage:', e);
  }
};

initializeStorage();

export const jubileeStorageService = {
  // Get all media items
  getAllMedia: () => {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.MEDIA);
      return data ? JSON.parse(data) : INITIAL_MEDIA_ITEMS;
    } catch (e) {
      console.error(e);
      return INITIAL_MEDIA_ITEMS;
    }
  },

  // Get by media type: 'video' | 'audio' | 'image' | 'document'
  getMediaByType: (type) => {
    const all = jubileeStorageService.getAllMedia();
    if (!type || type === 'all') return all;
    return all.filter((item) => item.mediaType.toLowerCase() === type.toLowerCase());
  },

  // Get single media item
  getMediaById: (id) => {
    const all = jubileeStorageService.getAllMedia();
    return all.find((item) => item.id === id) || null;
  },

  // Add media item
  addMediaItem: (item) => {
    try {
      const all = jubileeStorageService.getAllMedia();
      const newItem = {
        ...item,
        id: item.id || `jubilee-${Date.now()}`,
        uploadedAt: item.uploadedAt || new Date().toISOString(),
        status: item.status || 'active',
        visibility: item.visibility || 'public',
      };
      const updated = [newItem, ...all];
      localStorage.setItem(STORAGE_KEYS.MEDIA, JSON.stringify(updated));
      return newItem;
    } catch (e) {
      console.error('Failed to add media item', e);
      throw e;
    }
  },

  // Update media item
  updateMediaItem: (id, updates) => {
    try {
      const all = jubileeStorageService.getAllMedia();
      const index = all.findIndex((m) => m.id === id);
      if (index === -1) throw new Error('Item not found');
      all[index] = { ...all[index], ...updates, updatedAt: new Date().toISOString() };
      localStorage.setItem(STORAGE_KEYS.MEDIA, JSON.stringify(all));
      return all[index];
    } catch (e) {
      console.error('Failed to update media item', e);
      throw e;
    }
  },

  // Delete media item
  deleteMediaItem: (id) => {
    try {
      const all = jubileeStorageService.getAllMedia();
      const filtered = all.filter((m) => m.id !== id);
      localStorage.setItem(STORAGE_KEYS.MEDIA, JSON.stringify(filtered));
      return true;
    } catch (e) {
      console.error('Failed to delete media item', e);
      throw e;
    }
  },

  // Toggle visibility (public / restricted)
  toggleMediaVisibility: (id) => {
    try {
      const all = jubileeStorageService.getAllMedia();
      const index = all.findIndex((m) => m.id === id);
      if (index === -1) return null;
      all[index].visibility = all[index].visibility === 'public' ? 'restricted' : 'public';
      localStorage.setItem(STORAGE_KEYS.MEDIA, JSON.stringify(all));
      return all[index];
    } catch (e) {
      console.error(e);
      return null;
    }
  },

  // Events
  getEvents: () => {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.EVENTS);
      return data ? JSON.parse(data) : INITIAL_EVENTS;
    } catch (e) {
      return INITIAL_EVENTS;
    }
  },

  getEventById: (id) => {
    const events = jubileeStorageService.getEvents();
    return events.find((e) => e.id === id) || null;
  },

  addEvent: (event) => {
    try {
      const events = jubileeStorageService.getEvents();
      const newEvent = {
        ...event,
        id: event.id || `evt-${Date.now()}`,
      };
      const updated = [newEvent, ...events];
      localStorage.setItem(STORAGE_KEYS.EVENTS, JSON.stringify(updated));
      return newEvent;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },

  // Categories
  getCategories: () => {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.CATEGORIES);
      return data ? JSON.parse(data) : INITIAL_CATEGORIES;
    } catch (e) {
      return INITIAL_CATEGORIES;
    }
  },

  addCategory: (cat) => {
    try {
      const categories = jubileeStorageService.getCategories();
      const newCat = {
        ...cat,
        id: cat.id || `cat-${Date.now()}`,
      };
      const updated = [...categories, newCat];
      localStorage.setItem(STORAGE_KEYS.CATEGORIES, JSON.stringify(updated));
      return newCat;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },

  // Timeline decades
  getTimelineDecades: () => TIMELINE_DECADES,

  // Search
  searchMedia: ({ query = '', mediaType = 'all', eventId = '', categoryId = '', year = '', tag = '', speaker = '', visibility = 'all' } = {}) => {
    let items = jubileeStorageService.getAllMedia();

    if (visibility !== 'all') {
      items = items.filter((item) => item.visibility === visibility);
    }

    if (mediaType && mediaType !== 'all') {
      items = items.filter((item) => item.mediaType.toLowerCase() === mediaType.toLowerCase());
    }

    if (eventId && eventId !== 'all') {
      items = items.filter((item) => item.eventId === eventId);
    }

    if (categoryId && categoryId !== 'all') {
      items = items.filter((item) => item.categoryId === categoryId);
    }

    if (year && year !== 'all') {
      items = items.filter((item) => String(item.year) === String(year) || (item.date && item.date.startsWith(String(year))));
    }

    if (tag && tag !== 'all') {
      items = items.filter((item) => item.tags && item.tags.some((t) => t.toLowerCase() === tag.toLowerCase()));
    }

    if (speaker) {
      const spk = speaker.toLowerCase();
      items = items.filter((item) => (item.speaker && item.speaker.toLowerCase().includes(spk)) || (item.person && item.person.toLowerCase().includes(spk)));
    }

    if (query && query.trim()) {
      const q = query.trim().toLowerCase();
      items = items.filter((item) => {
        return (
          (item.title && item.title.toLowerCase().includes(q)) ||
          (item.description && item.description.toLowerCase().includes(q)) ||
          (item.eventName && item.eventName.toLowerCase().includes(q)) ||
          (item.categoryName && item.categoryName.toLowerCase().includes(q)) ||
          (item.person && item.person.toLowerCase().includes(q)) ||
          (item.speaker && item.speaker.toLowerCase().includes(q)) ||
          (item.author && item.author.toLowerCase().includes(q)) ||
          (item.fileName && item.fileName.toLowerCase().includes(q)) ||
          (item.source && item.source.toLowerCase().includes(q)) ||
          (item.year && String(item.year).includes(q)) ||
          (item.tags && item.tags.some((t) => t.toLowerCase().includes(q)))
        );
      });
    }

    return items;
  },

  // Get grouped search results for global search
  getGroupedSearchResults: (query = '', filters = {}) => {
    const results = jubileeStorageService.searchMedia({ ...filters, query });
    return {
      videos: results.filter((item) => item.mediaType === 'video'),
      audios: results.filter((item) => item.mediaType === 'audio'),
      images: results.filter((item) => item.mediaType === 'image'),
      documents: results.filter((item) => item.mediaType === 'document'),
      totalCount: results.length,
    };
  },

  // Get featured items
  getFeaturedMedia: () => {
    const all = jubileeStorageService.getAllMedia();
    return all.filter((item) => item.featured && item.visibility === 'public');
  },

  // Statistics
  getStats: () => {
    const all = jubileeStorageService.getAllMedia();
    const events = jubileeStorageService.getEvents();
    return {
      total: all.length,
      videos: all.filter((m) => m.mediaType === 'video').length,
      audios: all.filter((m) => m.mediaType === 'audio').length,
      images: all.filter((m) => m.mediaType === 'image').length,
      documents: all.filter((m) => m.mediaType === 'document').length,
      events: events.length,
      yearsCovered: '1951 - 2026 (75 Yrs)',
    };
  },

  // Reset to original seed data
  resetToDefaults: () => {
    localStorage.setItem(STORAGE_KEYS.MEDIA, JSON.stringify(INITIAL_MEDIA_ITEMS));
    localStorage.setItem(STORAGE_KEYS.EVENTS, JSON.stringify(INITIAL_EVENTS));
    localStorage.setItem(STORAGE_KEYS.CATEGORIES, JSON.stringify(INITIAL_CATEGORIES));
  },
};
